import { createFileRoute } from '@tanstack/react-router';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { SignupForm } from '@/components/auth/SignupForm';
import { redirectIfSignedIn } from '@/lib/use-session';

type SignupSearch = {
  // Clerk-issued ticket on invited signups. The Clerk Backend API returns a
  // URL of the form `${redirectUrl}?__clerk_ticket=…` when an invitation is
  // created with `notify=false`; landing here with that param means the
  // user is accepting a directory or admin invite.
  __clerk_ticket?: string;
  // Group-invite token forwarded from `/invites/:token` when a signed-out
  // visitor lands on a redeem URL. Stashed into Clerk's `unsafeMetadata`
  // during `signUp.create()` so the server's `user.created` webhook can
  // auto-redeem it after Clerk provisions the account.
  pendingInviteToken?: string;
  tenant?: string;
  invitation?: string;
};

export const Route = createFileRoute('/auth/signup')({
  beforeLoad: ({ context }) => redirectIfSignedIn(context),
  validateSearch: (search: Record<string, unknown>): SignupSearch => {
    const ticket = search['__clerk_ticket'];
    const pendingInviteToken = search['pendingInviteToken'];
    const t = search['tenant'];
    const i = search['invitation'];
    return {
      ...(typeof ticket === 'string' && ticket.length > 0
        ? { __clerk_ticket: ticket }
        : {}),
      ...(typeof pendingInviteToken === 'string' && pendingInviteToken.length > 0
        ? { pendingInviteToken }
        : {}),
      ...(typeof t === 'string' && t.length > 0 ? { tenant: t } : {}),
      ...(typeof i === 'string' && i.length > 0 ? { invitation: i } : {}),
    };
  },
  component: SignupPage,
});

function SignupPage() {
  const { __clerk_ticket: ticket, pendingInviteToken } = Route.useSearch();
  const { tenant, invitation } = Route.useSearch();
  return (
    <AuthLayout headline="Women in Circles are 2x more likely to receive promotions. Join the community now.">
      <SignupForm
        {...(ticket !== undefined ? { invitationTicket: ticket } : {})}
        {...(pendingInviteToken !== undefined
          ? { pendingInviteToken }
          : {})}
        {...(tenant !== undefined ? { initialTenantCode: tenant } : {})}
        {...(invitation !== undefined ? { initialInvitationId: invitation } : {})}
      />
    </AuthLayout>
  );
}
